import { Controller } from "@hotwired/stimulus";
import { Turbo } from "@hotwired/turbo-rails";

// Persists the filter params of an admin index page in localStorage and
// restores them when the page is visited without filter params in the URL.
//
// Usage:
//   <div data-controller="admin-filter-persistence"
//        data-admin-filter-persistence-key-value="models"
//        data-admin-filter-persistence-params-value="q,source,status,sort,dir">
//     <form ...>...</form>
//     <%= link_to "Clear", admin_models_path,
//         data: { action: "click->admin-filter-persistence#clear" } %>
//   </div>
export default class AdminFilterPersistenceController extends Controller {
  static values = {
    key: String,
    params: String,
  };

  declare readonly keyValue: string;
  declare readonly paramsValue: string;

  private get storageKey(): string {
    return `admin_filters:${this.keyValue}`;
  }

  private get declaredParams(): string[] {
    return this.paramsValue
      .split(",")
      .map((p) => p.trim())
      .filter((p) => p.length > 0);
  }

  connect(): void {
    const url = new URL(window.location.href);
    const search = url.searchParams;
    const declared = this.declaredParams;

    const nonEmpty = new URLSearchParams();
    let anyKeyPresent = false;
    for (const name of declared) {
      if (search.has(name)) {
        anyKeyPresent = true;
        const value = search.get(name) ?? "";
        if (value !== "") nonEmpty.append(name, value);
      }
    }

    if (Array.from(nonEmpty.keys()).length > 0) {
      this.write(nonEmpty.toString());
    } else if (anyKeyPresent) {
      this.write(null);
    } else {
      const saved = this.read();
      if (saved) {
        Turbo.visit(`${url.pathname}?${saved}`, { action: "replace" });
      }
    }
  }

  clear(_event: Event): void {
    this.write(null);
  }

  private read(): string | null {
    try {
      const value = window.localStorage.getItem(this.storageKey);
      return value && value.length > 0 ? value : null;
    } catch {
      return null;
    }
  }

  private write(value: string | null): void {
    try {
      if (value === null) {
        window.localStorage.removeItem(this.storageKey);
      } else {
        window.localStorage.setItem(this.storageKey, value);
      }
    } catch {
      // localStorage unavailable — no-op.
    }
  }
}
